import classNames from 'classnames';
import React, { forwardRef, use, useEffect, useImperativeHandle, useRef } from 'react';
import Helmet from 'react-helmet';

import { SiteContext } from 'src/context/site-context';
import { inlineImageAttribute } from 'src/enums/build/inline-image-attribute';
import {
  getObjectPosition,
  getSrc,
  getSrcSet,
  mobileBreakpointInPixels,
} from 'src/helpers/hosted-asset-helpers';

import { activeEnv } from '../../../build-scripts/environment';

import * as hostedImageStyles from './hosted-image.module.scss';

export type HostedImageDetails = Queries.ImageDetails & {
  mobileCrop?: Queries.ImageCrop;
  desktopCrop?: Queries.ImageCrop;
};

export type HostedImageProps = {
  imageDetails: HostedImageDetails | undefined | null;
  className?: string;
  loading?: 'eager' | 'lazy';
  onImageLoaded?: () => void;
  enablePreload?: boolean;
  imageWidthLimit?: number;
  enableInlineImages?: boolean;
};

const mobileCropMedia = `(max-width: ${mobileBreakpointInPixels}px)`;

const FALLBACK_INLINE_IMAGE_WIDTH = 100;

export const getRemoteUrlsToInline = (
  imagePath: string,
  { desktopCrop, mobileCrop }: Pick<HostedImageDetails, 'desktopCrop' | 'mobileCrop'>,
  imageWidthLimit = FALLBACK_INLINE_IMAGE_WIDTH
) => {
  const cropForMobile = mobileCrop ?? desktopCrop;
  return {
    src: getSrc(imagePath, { widthLimit: imageWidthLimit, crop: desktopCrop }),
    mobileSrc: cropForMobile
      ? getSrc(imagePath, { widthLimit: imageWidthLimit, crop: cropForMobile })
      : undefined,
  };
};

/**
 * We substitute the image's remote URL for a (base64) data URL using the following process:
 *  1. During static page generation, place a {@link inlineImageAttribute} on the img and source nodes.
 *  2. Afterward (see {@link onPostBuild}), download the remote images marked with that attribute, convert to base64,
 *     and put that data in both the HTML document and its corresponding page-data.json?v=2# file.
 * When the user loads their first page, the HTML document will already contain the inline image.
 * During/after hydration (including SPA navigation), the data URL will be found in the {@link SiteContext}.
 * Therefore, after hydration, this function can *expect* to find the data URL in the context (unless the page is
 * hot reloading); it won't find any data URLs during static page generation.
 */
const substituteDataUrlAfterHydration = (
  remoteUrl: string,
  inlineImageDataUrls?: Record<string, string>
) => {
  if (!remoteUrl) {
    return remoteUrl;
  }

  const foundDataUrl = inlineImageDataUrls?.[remoteUrl];
  if (!foundDataUrl) {
    if (typeof document !== 'undefined' && activeEnv !== 'development') {
      // Hydrating and definitely not hot reloading
      // eslint-disable-next-line no-console
      console.error(`Unable to find the data URL for ${remoteUrl} while hydrating!`);
    }
    return remoteUrl;
  }

  return foundDataUrl;
};

const HostedImage = forwardRef<HTMLImageElement, HostedImageProps>(
  (
    {
      imageDetails,
      className,
      loading,
      onImageLoaded = () => undefined,
      enablePreload = false,
      imageWidthLimit,
      enableInlineImages,
    },
    ref
  ) => {
    const { inlineImageDataUrls } = use(SiteContext);
    const innerRef = useRef<HTMLImageElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current!, []);

    const imagePath = imageDetails?.imagePath;

    useEffect(() => {
      if (
        innerRef.current?.complete &&
        innerRef.current.naturalWidth > 0 // was successfully loaded
      ) {
        onImageLoaded?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
    }, []);

    const inlineImageAttributes = enableInlineImages ? { [inlineImageAttribute]: '' } : {};

    if (!imageDetails || !imagePath) {
      return null;
    }

    const remoteUrlsToInline = enableInlineImages
      ? getRemoteUrlsToInline(imagePath, imageDetails, imageWidthLimit)
      : undefined;

    const src = remoteUrlsToInline
      ? substituteDataUrlAfterHydration(remoteUrlsToInline.src, inlineImageDataUrls)
      : getSrc(imagePath, { widthLimit: imageWidthLimit, crop: imageDetails.desktopCrop });

    const srcSet = enableInlineImages
      ? undefined
      : getSrcSet(imagePath, { widthLimit: imageWidthLimit, crop: imageDetails.desktopCrop });

    const cropForMobile = imageDetails.mobileCrop ?? imageDetails.desktopCrop;

    const mobileCropSrcSet = remoteUrlsToInline
      ? remoteUrlsToInline.mobileSrc &&
        substituteDataUrlAfterHydration(remoteUrlsToInline.mobileSrc, inlineImageDataUrls)
      : cropForMobile
        ? getSrcSet(imagePath, { widthLimit: imageWidthLimit, crop: cropForMobile })
        : undefined;

    return (
      <>
        {enablePreload && !enableInlineImages && (
          <Helmet>
            <link rel="preload" fetchPriority="high" as="image" href={src} imageSrcSet={srcSet} />
            {imageDetails.mobileCrop && (
              <link
                rel="preload"
                fetchPriority="high"
                as="image"
                href={src}
                imageSrcSet={mobileCropSrcSet}
                media={mobileCropMedia}
              />
            )}
          </Helmet>
        )}
        <picture>
          {imageDetails.mobileCrop && (
            <source srcSet={mobileCropSrcSet} media={mobileCropMedia} {...inlineImageAttributes} />
          )}
          <img
            ref={innerRef}
            className={classNames(hostedImageStyles.image, className)}
            style={
              {
                '--hosted-image-object-position': getObjectPosition(imageDetails),
              } as React.CSSProperties
            }
            srcSet={srcSet} /* srcSet must come before src */
            src={src}
            alt={imageDetails?.alternateText ?? ''}
            loading={loading}
            onLoad={onImageLoaded}
            {...inlineImageAttributes}
          />
        </picture>
      </>
    );
  }
);

HostedImage.displayName = 'HostedImage';

export default HostedImage;
